/* eslint-disable security/detect-object-injection */
/* eslint-disable no-debugger */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ObjectExtensions, ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import React from 'react';
import {
    Button,
    format,
    KeyCodes,
    Node,
    UncontrolledPagination,
    ITelemetryContent,
    getTelemetryObject
} from '@msdyn365-commerce-modules/utilities';
import RoofTableLinks, { ILinksData } from './lobas-roof-table-links';
import { ILobasRoofTableProps } from '../lobas-roof-table/lobas-roof-table.props.autogenerated';
import { ILobasRoofTableData } from '../lobas-roof-table/lobas-roof-table.data';
import LobasCheckBoxButton from './lobas-checkbox';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { SearchRefiner, SearchRefinerValue } from '@msdyn365-commerce/retail-proxy';
/**
 * Table Resources.
 */
export interface ILobasTableResources {}

/**
 * Table props.
 */
export interface ILobasDataTableProps {
    resources: ILobasTableResources;
    headings: (ILobasHeadingsProperty | undefined)[];
    subheadings: (ILobasHeadingsProperty | undefined)[];
    rows: ILobasTableRowProps[];
    className?: string;
    editLinkText?: string;
    actionLinkText?: string;
    deleteLinkText?: string;
    viewLinkText?: string;
    editLinkTooltip?: string;
    actionLinkTooltip?: string;
    deleteLinkTooltip?: string;
    viewLinkTooltip?: string;
    enableToModify: boolean;
    showCheckBoxes?: boolean;
    isSortable?: boolean;
    showPagination?: boolean;
    paginationProperty: ILobasPaginationProperty;
    minifyActions?: boolean;
    minifiedButtonText?: string;
    excludedColumns?: string[]; // Filled with ILobasTableItemProps.id of columns not to show
    isSingleSelectOnly: boolean;
    businessUserSelectCheckBoxAriaLabelText?: string;
    sortByAscending?: string;
    sortByDescending?: string;
    resourcePrefix?: string;
    actions: {
        onDelete?(userData: ILobasTableItemProps[], referenceBack?: HTMLButtonElement | null): void | undefined;
        onEdit?(userData: ILobasTableItemProps[], referenceBack?: HTMLButtonElement | null): void | undefined;
        onView?(userData: ILobasTableItemProps[]): void | undefined;
    };
    checkBoxCallback?(records: ILobasTableRowProps[]): void;
    formatPrice?(price: number): string;
    sourceProps: ILobasRoofTableProps<ILobasRoofTableData>;
    isRefinerMenuExpandedOnInitialLoad: boolean;
}

/**
 * Table IPaginationProperty props.
 */
export interface ILobasPaginationProperty {
    itemPerPage: number;
    skipCount: number;
    prevText: string;
    nextText: string;
    paginationText: string;
    url: string;
}

/**
 * Table ITableItemProps props.
 */
export interface ILobasTableItemProps {
    id: string;
    type: LobasTableDataType;
    value: boolean | number | string;
    className?: string;
    isLinkItem: boolean;
    links?: ILobasLinkProps[];
}

/**
 * Table IButtonReferences props.
 */
export interface ILobasButtonReferences {
    edit?: React.RefObject<HTMLButtonElement>;
    delete?: React.RefObject<HTMLButtonElement>;
}

/**
 * Table ITableRowProps props.
 */
export interface ILobasTableRowProps {
    row: ILobasTableItemProps[];
    isSelected: boolean;
    buttonReferences?: ILobasButtonReferences;
}

/**
 * Table IDataTableCells props.
 */
interface ILobasDataTableCells {
    header: boolean;
    className: string;
    content: string | number | React.ReactNode;
    cellIndex: number;
    scope: 'col' | 'row';
    colSpan?: number;
    records?: ILobasTableRowProps;
}

/**
 * Table IHeadingsProperty props.
 */
export interface ILobasHeadingsProperty {
    name: string;
    sortable: boolean;
    ariaLabel?: string;
    colspan?: number;
}

/**
 * Table ITableState props.
 */
export interface ILobasTableState {
    sortingKey: string;
    ascendingOrder: boolean;
    showActions: boolean;
    selectedIndex: string;
    refinerButtonExpanded: boolean;
}

export interface ILobasLinkProps {
    linkUrl: string;
    disabled: boolean;
    linkText?: string;
    linkType?: string;
}

export interface ILobasTableRefinerHierarchy extends SearchRefiner {
    /**
     * @description Array of children refiner values.
     */
    Values: ILobasTableRefinerValue[];
    SourceValue?: number;
}

export interface ILobasTableRefinerValue extends SearchRefinerValue {
    RefinerSourceValue?: number;
}

/**
 * Table TableDataType props.
 */
export enum LobasTableDataType {
    Number = 'Number',
    Text = 'Text',
    Price = 'Price'
}

export default class LobasTable extends React.PureComponent<ILobasDataTableProps, ILobasTableState> {
    public componentClassName: string = 'ms-table';

    private readonly headingPrefix: string = 'table';

    private readonly capitalizeHeadingPrefix: string = 'Table';

    constructor(props: ILobasDataTableProps) {
        super(props);

        const isExpanded = this.props.isRefinerMenuExpandedOnInitialLoad;

        this.state = {
            ascendingOrder: true,
            sortingKey: (props && props.headings[0] && props.headings[0].name) || '',
            showActions: props && props.minifyActions !== undefined ? !props.minifyActions : true,
            selectedIndex: '',
            refinerButtonExpanded: isExpanded
        };
    }

    public render(): JSX.Element {
        const { className, headings, subheadings, rows, isSortable, showPagination, paginationProperty } = this.props;
        const { sortingKey, ascendingOrder } = this.state;
        const classname = classnames(this.componentClassName, className);
        let tableRows = isSortable && sortingKey ? this._sortTableRow(rows, sortingKey, ascendingOrder) : rows;
        let paginationLinks = null;

        if (showPagination) {
            const { skipCount, itemPerPage, nextText, prevText, paginationText, url } = paginationProperty;

            paginationLinks = (
                <UncontrolledPagination
                    className='ms-table-business-org__pagination'
                    role='navigation'
                    aria-label={paginationText}
                    url={url}
                    qsp='skip'
                    items={tableRows.length}
                    itemsPerPage={itemPerPage}
                    startingItem={skipCount}
                    previousText={this._renderPrev(prevText)}
                    nextText={this._renderNext(nextText)}
                    previousAriaLabel={prevText}
                    nextAriaLabel={nextText}
                />
            );

            tableRows = this._getCurrentPageData(tableRows, skipCount, skipCount + itemPerPage);
        }

        const tableBody = this._renderRow(tableRows);
        const tableHead = this._renderHeadingRows(headings, subheadings);

        return (
            <div className={`${this.componentClassName}__container`}>
                <table className={classname}>
                    <thead>{tableHead}</thead>
                    <tbody>{tableBody}</tbody>
                </table>
                {paginationLinks}
            </div>
        );
    }

    /**
     * Set sorting key.
     * @param event -The mouse event.
     */
    private readonly _selectSortingKey = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent) => {
        const target = event.target as HTMLElement;
        let columnName = target.getAttribute('data-sort-name') ? (target.getAttribute('data-sort-name') as string) : '';

        if (!columnName) {
            columnName =
                target.parentElement && target.parentElement.getAttribute('data-sort-name')
                    ? (target.parentElement.getAttribute('data-sort-name') as string)
                    : '';
        }

        const { sortingKey, ascendingOrder } = this.state;
        let newOrder = true;

        if (sortingKey === columnName) {
            newOrder = !ascendingOrder;
        }

        this.setState({
            sortingKey: columnName,
            ascendingOrder: newOrder,
            showActions: false
        });
    };

    /**
     * Checkbox change.
     * @param rows -Table rows.
     * @param checkBoxCallback -Checkboxcallback function.
     * @param index -Index value for row.
     * @returns Checkbox rows.
     */
    private readonly _onCheckBoxChangeHandler = (
        rows: ILobasTableRowProps[],
        checkBoxCallback: (records: ILobasTableRowProps[]) => void,
        index?: number
    ) => () => {
        if (ObjectExtensions.isNumber(index)) {
            rows[index].isSelected = !rows[index].isSelected;
        }
        checkBoxCallback(rows);
    };

    private readonly _renderHeadingRows = (
        headerColumnData: (ILobasHeadingsProperty | undefined)[],
        subheaderColumnData: (ILobasHeadingsProperty | undefined)[]
    ) => {
        return [this._renderHeadingRow(headerColumnData), this._renderHeadingRow(subheaderColumnData)];
    };

    private readonly _renderHeadingRow = (columnData: (ILobasHeadingsProperty | undefined)[]) => {
        const {
            resources,
            showCheckBoxes,
            enableToModify,
            minifyActions,
            rows,
            actionLinkText,
            isSortable,
            checkBoxCallback,
            resourcePrefix,
            isSingleSelectOnly,
            businessUserSelectCheckBoxAriaLabelText,
            sortByAscending,
            sortByDescending
        } = this.props;
        const { sortingKey, ascendingOrder } = this.state;

        const sortingClassName = ascendingOrder ? 'asc' : 'dsc';

        const sortingValue = ascendingOrder ? sortByAscending : sortByDescending;

        const columns = columnData.map((data, cellIndex) => {
            if (!data) {
                return;
            }
            const counterAddition = 1;
            const updatedCellIndex = cellIndex + counterAddition;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- unsafe assignment.
            const heading =
                resources[`${this.headingPrefix}${data.name}HeadingText`] ||
                resources[`${resourcePrefix!}${this.capitalizeHeadingPrefix}${data.name}HeadingText`] ||
                resources[`${data.name}HeadingText`] ||
                data.name;

            const columnSortValue = sortingKey === data.name && sortingValue !== undefined ? sortingValue : '';

            const columnAriaLabel = data.ariaLabel ? `${format(data.ariaLabel, heading)} ${columnSortValue}` : '';

            const content = (isSortable && data.sortable && (
                <th
                    data-sort-name={data.name}
                    onClick={this._selectSortingKey}
                    onKeyUp={this._handleKeyPressPrev}
                    className={classnames(`${this.componentClassName}__heading-row-data`)}
                    key={`heading-${updatedCellIndex}`}
                    scope='col'
                    role='columnheader'
                    tabIndex={0}
                >
                    <div
                        className={classnames(`${this.componentClassName}__heading-sorting`, sortingKey === data.name && sortingClassName)}
                        aria-label={data.ariaLabel && columnAriaLabel.trim()}
                        aria-live='polite'
                    >
                        {heading}
                    </div>
                </th>
            )) || (
                <th scope='col' role='columnheader' tabIndex={0} colSpan={data.colspan}>
                    {heading}
                </th>
            );

            return content;
        });

        const radioCheckBox = showCheckBoxes && checkBoxCallback && !isSingleSelectOnly && (
            <LobasCheckBoxButton
                onChange={this._onCheckBoxChangeHandler(rows, checkBoxCallback)}
                name='All'
                ariaLabel={businessUserSelectCheckBoxAriaLabelText}
            />
        );

        const actionContent = (
            <div
                className={classnames(`${this.componentClassName}__heading-action`)}
                aria-label={minifyActions ? '' : actionLinkText}
                aria-live='polite'
            >
                {minifyActions ? '' : actionLinkText}
            </div>
        );

        return (
            <tr className={`${this.componentClassName}__heading-row`}>
                {showCheckBoxes &&
                    this.tableCell({
                        header: true,
                        className: `${this.componentClassName}__heading-row-data__checkbox checkbox`,
                        content: radioCheckBox,
                        cellIndex: -1,
                        scope: 'col'
                    })}
                {columns}
                {enableToModify &&
                    this.tableCell({
                        header: true,
                        className: `${this.componentClassName}__heading-row-data action-links`,
                        content: actionContent,
                        cellIndex: 1,
                        scope: 'col'
                    })}
            </tr>
        );
    };

    private readonly _renderRow = (rows: ILobasTableRowProps[]) => {
        const {
            enableToModify,
            showCheckBoxes,
            excludedColumns,
            actions,
            checkBoxCallback,
            formatPrice,
            isSingleSelectOnly,
            businessUserSelectCheckBoxAriaLabelText
        } = this.props;

        const tableRows = rows.map((rowsData, index) => {
            let className = index % 2 ? `${this.componentClassName}__row even-row` : `${this.componentClassName}__row odd-row`;
            let showActions = false;
            let additionalNameAriaLabel = '';
            let columnData: (JSX.Element | undefined)[] = [];
            const row = rowsData.row;

            if (row !== undefined && ArrayExtensions.hasElements(row)) {
                additionalNameAriaLabel = rowsData.row[0].value.toString();
                columnData = rowsData.row.map((cell, index2) => {
                    if (excludedColumns && excludedColumns.includes(cell.id)) {
                        return;
                    }

                    // Numbers/price data types will always be right-aligned per design standards
                    const extraClass = cell.type === LobasTableDataType.Number || cell.type === LobasTableDataType.Price ? 'num-type' : '';
                    let cellContent: any =
                        cell.type === LobasTableDataType.Price && formatPrice ? formatPrice(cell.value as number) : cell.value;
                    const columnSpecificClass = cell.className || '';

                    if (cell.isLinkItem) {
                        cellContent = (
                            <RoofTableLinks
                                {...{
                                    links: getLinksData(cell.links),
                                    telemetryContent: getTelemetryContent(this.props.sourceProps),
                                    role: 'link'
                                }}
                            />
                        );
                    }

                    return this.tableCell({
                        header: false,
                        className: classnames(`${this.componentClassName}__row-data`, extraClass, columnSpecificClass),
                        content: cellContent,
                        cellIndex: index2,
                        scope: 'row'
                    });
                });
            }

            const radioCheckBox = showCheckBoxes && checkBoxCallback && (
                <LobasCheckBoxButton
                    onChange={this._onCheckBoxChangeHandler(rows, checkBoxCallback, index)}
                    name='actionOptions'
                    value={`checkbox-${index}`}
                    isSingleSelectOnly={isSingleSelectOnly}
                    ariaLabel={`${businessUserSelectCheckBoxAriaLabelText!} ${additionalNameAriaLabel}`}
                />
            );
            const rowKey = `row-${index}`;

            if (rowKey === this.state.selectedIndex && this.state.showActions) {
                className += ' selected';
                showActions = true;
            }

            const actionButtons = actions && this._renderActions(rowsData.row, showActions, rowKey, rowsData.buttonReferences);

            return (
                <tr key={rowKey} className={className}>
                    {showCheckBoxes &&
                        this.tableCell({
                            header: false,
                            className: `${this.componentClassName}__row-data`,
                            content: radioCheckBox,
                            cellIndex: index,
                            scope: 'row',
                            records: rowsData
                        })}
                    {columnData}
                    {enableToModify &&
                        this.tableCell({
                            header: false,
                            className: `${this.componentClassName}__row-data`,
                            content: actionButtons,
                            cellIndex: index,
                            scope: 'row',
                            records: rowsData
                        })}
                </tr>
            );
        });

        return tableRows;
    };

    private readonly _renderActions = (
        data: ILobasTableItemProps[],
        showActions: boolean,
        key: string,
        buttonReference?: ILobasButtonReferences
    ): React.ReactNode => {
        const {
            editLinkText,
            deleteLinkText,
            viewLinkText,
            editLinkTooltip,
            deleteLinkTooltip,
            viewLinkTooltip,
            actionLinkTooltip,
            minifyActions,
            minifiedButtonText,
            actionLinkText,
            actions: { onEdit, onDelete, onView }
        } = this.props;
        const handleClickEdit =
            onEdit !== undefined
                ? () => {
                      onEdit(data, buttonReference?.edit?.current);
                  }
                : undefined;
        const handleClickDelete =
            onDelete !== undefined
                ? () => {
                      onDelete(data, buttonReference?.delete?.current);
                  }
                : undefined;
        const handleClickView =
            onView !== undefined
                ? () => {
                      onView(data);
                  }
                : undefined;

        const actionButtons = (
            <Node className={`${this.componentClassName}__row-links`}>
                {handleClickView && (
                    <Button className={`${this.componentClassName}__row-links-view`} aria-label={viewLinkTooltip} onClick={handleClickView}>
                        {viewLinkText}
                    </Button>
                )}
                {handleClickEdit && (
                    <Button
                        innerRef={buttonReference?.edit}
                        className={`${this.componentClassName}__row-links-edit`}
                        aria-label={editLinkTooltip}
                        onClick={handleClickEdit}
                    >
                        {editLinkText}
                    </Button>
                )}
                {handleClickDelete && (
                    <Button
                        innerRef={buttonReference?.delete}
                        className={`${this.componentClassName}__row-links-delete`}
                        aria-label={deleteLinkTooltip}
                        onClick={handleClickDelete}
                    >
                        {deleteLinkText}
                    </Button>
                )}
            </Node>
        );

        if (minifyActions) {
            return (
                <Node className={`${this.componentClassName}__row-links-minified`}>
                    <Button
                        className={`${this.componentClassName}__row-links-toggle`}
                        aria-label={actionLinkText}
                        data-type={key}
                        title={actionLinkTooltip}
                        onClick={this._toggleActions}
                        aria-expanded={this.state.showActions}
                    >
                        {minifiedButtonText ? minifiedButtonText : ''}
                    </Button>
                    {showActions && actionButtons}
                </Node>
            );
        }
        return actionButtons;
    };

    private readonly _toggleActions = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const target = event && (event.target as HTMLElement);
        const newKey = target && target.getAttribute('data-type') ? (target.getAttribute('data-type') as string) : '';
        this.setState({
            showActions: !this.state.showActions,
            selectedIndex: newKey
        });
    };

    private readonly _sortTableRow = (table: ILobasTableRowProps[], sortingkey: string, ascendingOrder: boolean): ILobasTableRowProps[] => {
        return table.sort((userOne: ILobasTableRowProps, userTwo: ILobasTableRowProps) => {
            const userOneData = userOne.row.find(cell => {
                return cell.id === sortingkey;
            });
            const userTwoData = userTwo.row.find(cell => {
                return cell.id === sortingkey;
            });

            if (!userOneData || !userTwoData) {
                return -1;
            }

            if (typeof userOneData.value === 'number' && typeof userTwoData.value === 'number') {
                return ascendingOrder ? userOneData.value - userTwoData.value : userTwoData.value - userOneData.value;
            }

            const cleanFirst =
                (userOneData.value as string) &&
                userOneData.value
                    .toString()
                    .toLowerCase()
                    .trim();
            const cleanSecond =
                (userOneData.value as string) &&
                userTwoData.value
                    .toString()
                    .toLowerCase()
                    .trim();

            if (!cleanFirst || !cleanSecond) {
                return -1;
            }
            if (ascendingOrder) {
                return cleanFirst < cleanSecond ? -1 : 1;
            }
            return cleanFirst > cleanSecond ? -1 : 1;
        });
    };

    private readonly _getCurrentPageData = (tableRows: ILobasTableRowProps[], start: number, end: number): ILobasTableRowProps[] => {
        if (start >= end) {
            return tableRows;
        }

        const result = [];
        for (let i = start; i < end; i++) {
            tableRows[i] && result.push(tableRows[i]);
        }

        return result;
    };

    private readonly tableCell = (props: ILobasDataTableCells) => {
        const { className, header, cellIndex, scope, colSpan, content } = props;
        const hiddenTabIndex = -1;
        const initialTabIndex = 0;
        return header ? (
            <th
                className={className}
                key={`heading-${cellIndex}`}
                scope={scope}
                colSpan={colSpan}
                tabIndex={cellIndex > initialTabIndex ? initialTabIndex : hiddenTabIndex}
            >
                {content}
            </th>
        ) : (
            <td className={className} scope={scope} colSpan={colSpan}>
                {content}
            </td>
        );
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-table-business-org__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-table-business-org__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    /**
     * Calls method display result.
     * @param event - Keyboard event.
     */
    private readonly _handleKeyPressPrev = (event: React.KeyboardEvent): void => {
        if (event.keyCode === KeyCodes.Enter || event.keyCode === KeyCodes.Space) {
            this._selectSortingKey(event);
        }
    };
}

export function getLinksData(props: ILobasLinkProps[] | undefined): ILinksData[] | undefined {
    const linksData =
        props &&
        props.map(
            item =>
                ({
                    linkUrl: { destinationUrl: `${item.linkUrl}`, type: `${item.linkType}` } as Msdyn365.ILinkData,
                    linkText: `${item.linkText}`,
                    disabled: item.disabled,
                    openInNewTab: true
                } as ILinksData)
        );
    return linksData;
}

export function getTelemetryContent(props: ILobasRoofTableProps<ILobasRoofTableData>): ITelemetryContent {
    return getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
}
